import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './VideoEditor.css';

// =============== 类型定义 ===============
interface JsonField {
  key: string;
  name: string;
  type: number;
  required: number;
  value?: string | number;
  options?: string[]; // 明确为字符串数组
}

interface ApiConfigItem {
  id: string;
  aid?: string;
  type: string;
  resolution: string;
  duration: string;
  credits: number;
  json: JsonField[];
}

interface RawModelFromAPI {
  id: string;
  name: string;
  model: string;
  description: string;
  i2v: string;
  t2v: string;
  apiConfig: ApiConfigItem[];
}

interface TransformedModel {
  id: string;
  model: string; 
  name: string;
  description: string;
  i2v: boolean;
  t2v: boolean;
  apiConfig: ApiConfigItem[];
}

interface VideoEditorProps {
  onGenerate: (settings: any) => void;
  isGenerating: boolean;
  settings: {
    json: JsonField[];
    prompt: string;
    model: string;
    duration: string;
    resolution: string;
    aid: string;
    templateName?: string;
  };
  // onPromptChange?: (prompt: string) => void;
}

// =============== 工具函数 ===============
function transformModel(raw: RawModelFromAPI): TransformedModel {
  const safeParseJson = (jsonStr: string): JsonField[] => {
    try {
      if (!jsonStr || typeof jsonStr !== 'string') return [];
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to parse apiConfig.json:', jsonStr, error);
      return [];
    }
  };

  return {
    id: raw.id,
    model: raw.model,
    name: raw.name,
    description: raw.description,
    i2v: raw.i2v === '1',
    t2v: raw.t2v === '1',
    apiConfig: raw.apiConfig.map(config => ({
      ...config,
      json: safeParseJson(config.json as unknown as string),
    })),
  };
}

// 🔧 辅助：将 JsonField[] 转为 Record<string, string | number>
const flattenJsonFields = (json: JsonField[]): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  json.forEach(field => {
    if (field.key) {
      result[field.key] = field.value ?? '';
    }
  });
  return result;
};

// =============== 组件主体 ===============
const VideoEditor: React.FC<VideoEditorProps> = ({ 
  onGenerate, 
  isGenerating, 
  settings,
  // onPromptChange
}) => {
  // ✅ 初始化 localSettings 仅一次（不再监听 settings 变化）
  const initialLocalSettings = {
    prompt: settings.prompt,
    model: settings.model,
    duration: settings.duration,
    resolution: settings.resolution,
    aid: settings.aid || '',
    ...flattenJsonFields(settings.json || []),
  };

  const [localSettings, setLocalSettings] = useState(initialLocalSettings);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDurationMenu, setShowDurationMenu] = useState(false);
  const [showResolutionMenu, setShowResolutionMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [modelMenuPosition, setModelMenuPosition] = useState({ top: 0, left: 0 });
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [models, setModels] = useState<TransformedModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [openCustomMenus, setOpenCustomMenus] = useState<Record<string, boolean>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const durationRef = useRef<HTMLDivElement>(null);
  const resolutionRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);
  const modelAreaRef = useRef<HTMLDivElement>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);

  // ❌ 删除了自动同步 settings 的 useEffect！

  // 🔁 获取模型
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/face-swap/list`);
        if (!res.ok) throw new Error('Failed to fetch models');
        const data = await res.json();
        const rawData: RawModelFromAPI[] = data['data'];
        const transformed = rawData.map(transformModel);
        setModels(transformed);
      } catch (error) {
        console.error('Error fetching models:', error);
        setModels([]);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  // 🧠 可用模型
  const availableModels = useMemo(() => {
    return models.filter(model => uploadedImage ? model.i2v : model.t2v);
  }, [models, uploadedImage]);

  const currentModel = useMemo(() => {
    return availableModels.find(m => m.id === localSettings.model) || availableModels[0] || null;
  }, [availableModels, localSettings.model]);

  const availableResolutions = useMemo(() => {
    if (!currentModel) return [];
    const resSet = new Set<string>();
    currentModel.apiConfig.forEach(c => resSet.add(c.resolution));
    return Array.from(resSet).sort();
  }, [currentModel]);

  const availableDurations = useMemo(() => {
    if (!currentModel || !localSettings.resolution) return [];
    const durSet = new Set<string>();
    currentModel.apiConfig
      .filter(c => c.resolution === localSettings.resolution)
      .forEach(c => durSet.add(c.duration));
    return Array.from(durSet).sort((a, b) => parseInt(a) - parseInt(b));
  }, [currentModel, localSettings.resolution]);

  const currentApiConfig = useMemo(() => {
    return currentModel?.apiConfig.find(c => c.id === localSettings.aid) || null;
  }, [currentModel, localSettings.aid]);

  const currentCredits = currentApiConfig ? currentApiConfig.credits : 0;

  const needsImage = useMemo(() => {
    return currentApiConfig?.json.some(field => field.key === 'image') || false;
  }, [currentApiConfig]);

  const needsPrompt = useMemo(() => {
    return !currentApiConfig || currentApiConfig.json.some(field => field.key === 'prompt');
  }, [currentApiConfig]);

  // 🔄 自动修复无效设置（基于本地选择）
  useEffect(() => {
    if (availableModels.length === 0 || loadingModels) return;

    let newModelId = localSettings.model;
    let newRes = localSettings.resolution;
    let newDur = localSettings.duration;
    let newAid = localSettings.aid;

    const isValidModel = availableModels.some(m => m.id === newModelId);
    if (!isValidModel) {
      const firstModel = availableModels[0];
      const firstConfig = firstModel.apiConfig[0];
      newModelId = firstModel.id;
      newRes = firstConfig.resolution;
      newDur = firstConfig.duration;
      newAid = firstConfig.id;
    } else {
      const model = availableModels.find(m => m.id === newModelId)!;
      const match = model.apiConfig.find(
        c => c.resolution === newRes && c.duration === newDur
      );
      if (match) {
        newAid = match.id;
      } else {
        const fallback = model.apiConfig[0];
        newRes = fallback.resolution;
        newDur = fallback.duration;
        newAid = fallback.id;
      }
    }

    // 获取新 config 的 json 并扁平化
    const selectedModel = availableModels.find(m => m.id === newModelId);
    const selectedConfig = selectedModel?.apiConfig.find(c => c.id === newAid) || selectedModel?.apiConfig[0];
    const newJsonValues = selectedConfig ? flattenJsonFields(selectedConfig.json) : {};

    setLocalSettings(prev => {
      const changes: Partial<typeof prev> = {};
      if (prev.model !== newModelId) changes.model = newModelId;
      if (prev.resolution !== newRes) changes.resolution = newRes;
      if (prev.duration !== newDur) changes.duration = newDur;
      if (prev.aid !== newAid) changes.aid = newAid;
      // 合并新的 json 字段（覆盖旧的）
      Object.assign(changes, newJsonValues);

      if (Object.keys(changes).length > 0) {
        return { ...prev, ...changes };
      }
      return prev;
    });
  }, [availableModels, loadingModels, localSettings.model]);

  //r2Upload
  const uploadToR2 = async (file: File): Promise<string> => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('glistar_token');
      // Simulate calling backend upload API, return R2 URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/site/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({type:file.type}),
      });

      const data = await response.json();

      if (!response.ok) throw new Error('Upload failed');

      // 2. Upload directly to R2
      const uploadResponse = await fetch(data.data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          // 'Authorization': `Bearer ${token}`,
          'Content-Type': file.type,
        },
      });

      console.log(response,data,'uplodfile')

      if (uploadResponse.ok) {
        return `https://cdn.glistar.ai/${data.data.fileKey}`; 
      }  else {
        throw new Error('R2 upload failed');
      }

    } catch (error) {
      console.error('R2 upload error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ====== 事件处理 ======
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = await uploadToR2(file);
      setUploadedImage(imageUrl);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = event.dataTransfer.files;
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      const imageUrl = await uploadToR2(files[0]);
      setUploadedImage(imageUrl);
    }
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newPrompt = event.target.value;
    setLocalSettings(prev => ({ ...prev, prompt: newPrompt }));
    // if (onPromptChange) onPromptChange(newPrompt);
  };

  const handleEnhance = async () => {
    if (!localSettings.prompt.trim()) {
      alert('Please enter a prompt first');
      return;
    }
    setIsEnhancing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const enhancedPrompt = `${localSettings.prompt} - with cinematic lighting, professional composition, and stunning visual effects`;
      setLocalSettings(prev => ({ ...prev, prompt: enhancedPrompt }));
      // if (onPromptChange) onPromptChange(enhancedPrompt);
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      alert('Failed to enhance prompt. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleModelChange = (modelId: string) => {
    const selectedModel = availableModels.find(m => m.id === modelId);
    if (selectedModel) {
      const firstConfig = selectedModel.apiConfig[0];
      const jsonValues = flattenJsonFields(firstConfig.json);
      setLocalSettings({
        prompt: localSettings.prompt,
        model: modelId,
        resolution: firstConfig.resolution,
        duration: firstConfig.duration,
        aid: firstConfig.id,
        ...jsonValues,
      });
    }
  };

  const handleResolutionChange = (resolution: string) => { 
    const selectedModel = availableModels.find(m => m.id === localSettings.model); 
    if (selectedModel) {
      const match = selectedModel.apiConfig.find(c => c.resolution === resolution); 
      const config = match || selectedModel.apiConfig[0];
      console.log('Changing resolution to:', config);
      const jsonValues = flattenJsonFields(config.json);
      setLocalSettings({
        ...localSettings,
        resolution,
        duration: config.duration,
        aid: config.id,
        ...jsonValues,
      });
    }
  };

  const handleDurationChange = (duration: string) => {
    const selectedModel = availableModels.find(m => m.id === localSettings.model);
    if (selectedModel) {
      const match = selectedModel.apiConfig.find(
        c => c.resolution === localSettings.resolution && c.duration === duration
      );
      const config = match || selectedModel.apiConfig[0];
      const jsonValues = flattenJsonFields(config.json);
      setLocalSettings({
        ...localSettings,
        duration,
        aid: config.id,
        ...jsonValues,
      });
    }
  };

  const handleGenerate = () => {
    if (!currentApiConfig) {
      alert('No valid configuration selected.');
      return;
    }

    const errors: string[] = [];

    // ✅ 合并最新数据：以 uploadedImage 为准覆盖 localSettings.image
    const finalSettings = {
      ...localSettings,
      image: uploadedImage, // 优先用 uploadedImage
    };


    // 遍历当前 API 配置中的 json 字段
    currentApiConfig.json.forEach((field) => {
      if (field.required === 1) {
        const value = (finalSettings as Record<string, any>)[field.key];

        // 判断是否为空：null, undefined, 空字符串，或仅空白字符
        if (value == null || (typeof value === 'string' && value.trim() === '')) {
          errors.push(`${field.name || field.key} is required`);
        }
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    onGenerate(finalSettings); // ✅ 包含所有自定义字段！
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // ====== 关闭菜单 ======
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (modelRef.current && !modelRef.current.contains(target) &&
          modelMenuRef.current && !modelMenuRef.current.contains(target)) {
        setShowModelMenu(false);
      }
      if (durationRef.current && !durationRef.current.contains(target)) {
        setShowDurationMenu(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(target)) {
        setShowResolutionMenu(false);
      }
      // 关闭所有 custom menus
      Object.keys(openCustomMenus).forEach(key => {
        setOpenCustomMenus(prev => ({ ...prev, [key]: false }));
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCustomMenus]);

  // ====== 渲染 ======
  if (loadingModels) {
    return (
      <div className="video-editor">
        <div className="loading-message">Loading models...</div>
      </div>
    );
  }

  return (
    <div className="video-editor">
      {/* Upload Image */}
      {needsImage && (
        <div className="upload-section">
          <div 
            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploadedImage ? (
              <div className="uploaded-image">
                <img src={uploadedImage} alt="Uploaded" />
                <div className="image-overlay">
                  <button 
                    className="change-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div className="upload-placeholder">
                <div className="upload-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="upload-text">Upload Image</span>
                <span className="drag-hint">Drag image here or click to upload</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Prompt */}
      {needsPrompt && (
        <div className="prompt-section">
          <div className="prompt-container">
            <div className="prompt-header">
              <div className="prompt-header-top">
                <span className="prompt-label">Prompt</span>
                {settings.templateName && (
                  <span className="template-indicator">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                    {settings.templateName}
                  </span>
                )}
              </div>
              {!isPromptFocused && !localSettings.prompt && (
                <span className="prompt-hint">Describe your vision in detail</span>
              )}
            </div>
            <textarea
              value={localSettings.prompt}
              onChange={handlePromptChange}
              onFocus={() => setIsPromptFocused(true)}
              onBlur={() => setIsPromptFocused(false)}
              placeholder=""
              className="prompt-textarea"
              rows={4}
            />
            <div className="enhance-section">
              <button 
                className={`enhance-btn ${isEnhancing ? 'enhancing' : ''}`}
                onClick={handleEnhance}
                disabled={isEnhancing || !localSettings.prompt.trim()}
              >
                {isEnhancing ? (
                  <>
                    <div className="enhance-spinner"></div>
                    <span>Enhancing...</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.5 2L17.5 8L23.5 10L17.5 12L15.5 18L13.5 12L7.5 10L13.5 8L15.5 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 16L8 19L11 20L8 21L7 24L6 21L3 20L6 19L7 16Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Enhance</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Fields from JSON */}
      {currentApiConfig?.json
        .filter(field => !['image', 'prompt', 'duration', 'resolution'].includes(field.key))
        .map((field) => {
          const fieldValue = (localSettings as any)[field.key] ?? field.value ?? '';

          if (field.type === 4 && Array.isArray(field.options)) {
            const isOpen = openCustomMenus[field.key] || false;

            return (
              <div key={field.key} className="custom-fields-row">
                <div className="custom-field-section">
                  <div
                    className="custom-field-area"
                    onClick={() => {
                      setOpenCustomMenus(prev => ({
                        ...Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {}),
                        [field.key]: !prev[field.key]
                      }));
                    }}
                  >
                    <span className="custom-field-text">{capitalizeFirstLetter(field.name)}</span>
                    <span className="custom-field-value">{String(fieldValue)}</span>
                  </div>

                  {isOpen && (
                    <div className="custom-field-menu">
                      {field.options.map(opt => {
                        const isSelected = opt === fieldValue;
                        return (
                          <div
                            key={opt}
                            className={`custom-field-option ${isSelected ? 'selected' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocalSettings(prev => ({
                                ...prev,
                                [field.key]: opt // ✅ 直接更新到顶层
                              }));
                              setOpenCustomMenus({ [field.key]: false });
                            }}
                          >
                            {opt}
                            {isSelected && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          return null;
        })}

      {/* Model Selection */}
      <div className="model-section" ref={modelRef}>
        <div 
          ref={modelAreaRef}
          className="model-area"
          onClick={() => {
            if (!showModelMenu && modelAreaRef.current) {
              const rect = modelAreaRef.current.getBoundingClientRect();
              const menuHeight = Math.min(availableModels.length * 60 + 20, 500);
              const viewportHeight = window.innerHeight;
              let topPosition = rect.top - menuHeight;
              if (topPosition < 20) topPosition = 20;
              if (topPosition + menuHeight > viewportHeight - 20) {
                topPosition = viewportHeight - menuHeight - 20;
              }
              setModelMenuPosition({
                top: topPosition,
                left: rect.right + 8
              });
            }
            setShowModelMenu(!showModelMenu);
          }}
        >
          <span className="model-text">Model</span>
          <span className="model-value">
            {availableModels.find(m => m.id === localSettings.model)?.name || localSettings.model}
          </span>
        </div>
        {showModelMenu && createPortal(
          <div 
            ref={modelMenuRef}
            className="model-menu" 
            style={{ 
              top: `${modelMenuPosition.top}px`, 
              left: `${modelMenuPosition.left}px` 
            }}
          >
            {availableModels.map((model) => (
              <div
                key={model.id}
                className={`model-option ${localSettings.model === model.id ? 'selected' : ''}`}
                onClick={() => {
                  handleModelChange(model.id);
                  setShowModelMenu(false);
                }}
              >
                <div className="model-option-header">
                  <div className="model-option-title">
                    <span className="model-option-name">{model.name}</span>
                  </div>
                  {localSettings.model === model.id && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div className="model-option-description">{model.description}</div>
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>

      {/* Resolution and Duration */}
      <div className="resolution-duration-row">
        <div className="resolution-section" ref={resolutionRef}>
          <div 
            className="resolution-area"
            onClick={() => setShowResolutionMenu(!showResolutionMenu)}
          >
            <span className="resolution-text">Resolution</span>
            <span className="resolution-value">{localSettings.resolution}</span>
          </div>
          {showResolutionMenu && (
            <div className="resolution-menu">
              {availableResolutions.map((res) => (
                <div
                  key={res}
                  className={`resolution-option ${localSettings.resolution === res ? 'selected' : ''}`}
                  onClick={() => {
                    handleResolutionChange(res);
                    setShowResolutionMenu(false);
                  }}
                >
                  {res}
                  {localSettings.resolution === res && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="duration-section" ref={durationRef}>
          <div 
            className="duration-area"
            onClick={() => setShowDurationMenu(!showDurationMenu)}
          >
            <span className="duration-text">Duration</span>
            <span className="duration-value">{localSettings.duration}</span>
          </div>
          {showDurationMenu && (
            <div className="duration-menu">
              {availableDurations.map((dur) => (
                <div
                  key={dur}
                  className={`duration-option ${localSettings.duration === dur ? 'selected' : ''}`}
                  onClick={() => {
                    handleDurationChange(dur);
                    setShowDurationMenu(false);
                  }}
                >
                  {dur}
                  {localSettings.duration === dur && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate Button */}
      <div className="generate-section">
        <button 
          className={`generate-btn ${isGenerating ? 'generating' : ''}`}
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <div className="loading-spinner"></div>
              Generating...
            </>
          ) : (
            <>
              <span>Generate</span>
              {currentCredits > 0 && (
                <div className="credits-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                  <span>{currentCredits}</span>
                </div>
              )}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VideoEditor;